/**
 * Shared display-only formatting for the discount engine screens (phase 6b,
 * Task 7b tidy). Hoisted out of `promotions/PromotionsList.tsx` and
 * `promotions/CodesTab.tsx`, which each had their own copy (`fmtDate` /
 * `fmtDateTime`) plus a private `KIND_LABEL`; `PromotionDetail.tsx` had a
 * third `KIND_LABEL` and imported `fmtDateTime` from the leaf module
 * `CodesTab.tsx` only to dodge a `PromotionDetail ⇄ CodesTab` import cycle.
 * A standalone module has no such cycle, so every user imports from here.
 * `discountLabel` / `managedOn` followed (fix wave minor 7): they were
 * exported from `PromotionsList.tsx` and imported by `PromotionDetail.tsx`,
 * a screen reaching into another screen for a pure formatter.
 */
import { formatRand } from './money';
import type { PromotionKind, PromotionRow } from './types';

export const KIND_LABEL: Record<PromotionKind, string> = {
  MANUAL: 'Manual',
  REFERRAL: 'Referral',
  REFERRAL_REWARD: 'Referral reward',
  LOYALTY_REWARD: 'Loyalty reward',
  AUTO_CART: 'Auto cart',
};

/** Display-only timestamp; `null`/`undefined`/invalid -> em dash. Never used for arithmetic. */
export const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
};

/** Where a non-MANUAL (seeded singleton) promotion is actually edited (P6-1). */
export const managedOn = (kind: PromotionKind): string | null => {
  if (kind === 'MANUAL') return null;
  if (kind === 'LOYALTY_REWARD') return 'Managed on Loyalty';
  if (kind === 'REFERRAL' || kind === 'REFERRAL_REWARD') return 'Managed on Referrals';
  return 'Read-only';
};

/** Display-only: never arithmetic (money.ts). */
export const discountLabel = (p: Pick<PromotionRow, 'discountType' | 'discountValue' | 'freeItemQty'>): string => {
  switch (p.discountType) {
    case 'AMOUNT':
      return formatRand(p.discountValue);
    case 'PERCENT':
      // Trim only a decimal tail ("20.00" -> "20", "12.50" -> "12.5"); string ops only, never Number().
      return p.discountValue == null ? '—' : `${p.discountValue.replace(/\.(\d*?)0+$/, '.$1').replace(/\.$/, '')}%`;
    case 'FREE_DELIVERY':
      return 'Free delivery';
    case 'FREE_ITEM':
      return p.freeItemQty ? `Free item ×${p.freeItemQty}` : 'Free item';
    default:
      return '—';
  }
};
